import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';
import type { DashboardExecuteResponse } from '../lib/dashboards-client.js';

function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => String(values[key] ?? ''));
}

/** E-1: Format an ISO timestamp as a short relative or absolute string using Intl APIs. */
function formatExecutedAt(isoTimestamp: string): string {
  const date = new Date(isoTimestamp);
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);

  if (diffSecs < 60) {
    return new Intl.RelativeTimeFormat('es', { numeric: 'auto' }).format(-diffSecs, 'second');
  }
  const diffMins = Math.floor(diffSecs / 60);
  if (diffMins < 60) {
    return new Intl.RelativeTimeFormat('es', { numeric: 'auto' }).format(-diffMins, 'minute');
  }
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) {
    return new Intl.RelativeTimeFormat('es', { numeric: 'auto' }).format(-diffHours, 'hour');
  }
  return new Intl.DateTimeFormat('es', { dateStyle: 'short', timeStyle: 'short' }).format(date);
}

interface DashboardHeaderProps {
  title: string;
  executionResult: DashboardExecuteResponse | undefined;
  isLoading: boolean;
  onRefreshAll: () => void;
}

export function DashboardHeader({
  title,
  executionResult,
  isLoading,
  onRefreshAll,
}: DashboardHeaderProps) {
  const meta = executionResult?.dashboardMeta;

  return (
    <header
      className={cn(
        'sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3',
        'border-b border-[var(--color-qup-border-dark)] bg-[var(--color-qup-bg-dark)] px-4 py-3',
      )}
    >
      <div className="min-w-0">
        <h1 className="truncate text-lg font-semibold text-[var(--color-qup-text-dark)]">
          {title}
        </h1>
        {meta !== undefined && (
          <p className="mt-0.5 flex flex-wrap gap-2 text-xs text-[var(--color-qup-text-muted-dark)]">
            <span>{interpolate(t('dashboards.meta_widgets'), { count: meta.totalWidgets })}</span>
            <span className="text-green-400">
              {interpolate(t('dashboards.meta_succeeded'), { count: meta.succeeded })}
            </span>
            {meta.failed > 0 && (
              <span className="text-red-400">
                {interpolate(t('dashboards.meta_failed'), { count: meta.failed })}
              </span>
            )}
            <span>{interpolate(t('dashboards.meta_duration'), { ms: meta.totalDurationMs })}</span>
            <span data-testid="executed-at" title={meta.executedAt}>
              {t('dashboards.executed_at')}: {formatExecutedAt(meta.executedAt)}
            </span>
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={onRefreshAll}
        disabled={isLoading}
        className={cn(
          'inline-flex items-center gap-2 rounded-[var(--radius-button)]',
          'bg-[var(--color-qup-primary)] px-4 py-2 text-sm font-medium text-white',
          'hover:bg-[var(--color-qup-primary-hover)] transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-qup-primary)]/50',
          'disabled:pointer-events-none disabled:opacity-50',
        )}
        aria-label={t('dashboards.refresh_all')}
      >
        <svg
          className={cn('h-4 w-4', isLoading && 'animate-spin')}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
        {t('dashboards.refresh_all')}
      </button>
    </header>
  );
}
